import { Link } from 'react-router-dom';
import { Briefcase } from 'lucide-react';

const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-blue-900" />
              <span className="text-xl font-bold text-blue-900">JobSuiza</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Política de Cookies</h1>
        <p className="text-sm text-slate-600 mb-8">Última actualización: {new Date().toLocaleDateString('es-ES')}</p>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. ¿Qué son las Cookies?</h2>
            <p className="text-slate-700 leading-relaxed">
              Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando visita un sitio web. 
              Nos permiten reconocerle, recordar sus preferencias y mejorar su experiencia de navegación.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Cómo Usamos las Cookies</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              JobSuiza utiliza cookies para diversos propósitos, incluyendo:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>Mantener su sesión de usuario activa</li>
              <li>Recordar sus preferencias de búsqueda y filtros</li>
              <li>Analizar el uso del sitio web para mejorarlo</li>
              <li>Mostrar publicidad relevante (Google AdSense)</li>
              <li>Proporcionar autenticación segura</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Tipos de Cookies que Utilizamos</h2>

            <div className="space-y-6">
              <div className="border-l-4 border-blue-900 pl-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">3.1 Cookies Estrictamente Necesarias</h3>
                <p className="text-slate-700 leading-relaxed mb-3">
                  <strong>Propósito:</strong> Esenciales para el funcionamiento del sitio web
                </p>
                <p className="text-slate-700 leading-relaxed mb-3">
                  <strong>Consentimiento requerido:</strong> No (necesarias para el servicio)
                </p>
                <div className="bg-slate-50 p-4 rounded">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2">Cookie</th>
                        <th className="text-left py-2">Duración</th>
                        <th className="text-left py-2">Descripción</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 font-mono">session_token</td>
                        <td className="py-2">7 días</td>
                        <td className="py-2">Autenticación de usuario (Google OAuth)</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 font-mono">token</td>
                        <td className="py-2">30 días</td>
                        <td className="py-2">Autenticación JWT (email/password)</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-mono">cookieConsent</td>
                        <td className="py-2">365 días</td>
                        <td className="py-2">Almacena sus preferencias de cookies</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="border-l-4 border-emerald-600 pl-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">3.2 Cookies de Rendimiento y Análisis</h3>
                <p className="text-slate-700 leading-relaxed mb-3">
                  <strong>Propósito:</strong> Analizar cómo los visitantes usan el sitio
                </p>
                <p className="text-slate-700 leading-relaxed mb-3">
                  <strong>Consentimiento requerido:</strong> Sí
                </p>
                <div className="bg-slate-50 p-4 rounded">
                  <p className="text-slate-700 mb-2"><strong>Google Analytics</strong></p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    <li><span className="font-mono">_ga</span> - 2 años - Identificador único de usuario</li>
                    <li><span className="font-mono">_gid</span> - 24 horas - Identificador de sesión</li>
                    <li><span className="font-mono">_gat</span> - 1 minuto - Limitación de tasa de solicitudes</li>
                  </ul>
                  <p className="text-sm text-slate-600 mt-3">
                    <strong>Proveedor:</strong> Google LLC | 
                    <strong> Política:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-900 underline">Ver política</a>
                  </p>
                </div>
              </div>

              <div className="border-l-4 border-yellow-500 pl-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">3.3 Cookies de Publicidad</h3>
                <p className="text-slate-700 leading-relaxed mb-3">
                  <strong>Propósito:</strong> Mostrar anuncios relevantes
                </p>
                <p className="text-slate-700 leading-relaxed mb-3">
                  <strong>Consentimiento requerido:</strong> Sí
                </p>
                <div className="bg-slate-50 p-4 rounded">
                  <p className="text-slate-700 mb-2"><strong>Google AdSense</strong></p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    <li><span className="font-mono">__gads</span> - 13 meses - Anuncios personalizados</li>
                    <li><span className="font-mono">__gac</span> - 90 días - Información de campaña</li>
                    <li><span className="font-mono">IDE</span> - 24 meses - Identificador de publicidad (DoubleClick)</li>
                  </ul>
                  <p className="text-sm text-slate-600 mt-3">
                    <strong>Proveedor:</strong> Google LLC | 
                    <strong> Política:</strong> <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-blue-900 underline">Ver política</a>
                  </p>
                </div>
              </div>

              <div className="border-l-4 border-purple-600 pl-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">3.4 Cookies de Funcionalidad</h3>
                <p className="text-slate-700 leading-relaxed mb-3">
                  <strong>Propósito:</strong> Recordar preferencias del usuario
                </p>
                <p className="text-slate-700 leading-relaxed mb-3">
                  <strong>Consentimiento requerido:</strong> No (mejoran experiencia pero no rastrean)
                </p>
                <div className="bg-slate-50 p-4 rounded">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 font-mono">searchFilters</td>
                        <td className="py-2">30 días</td>
                        <td className="py-2">Preferencias de búsqueda y filtros</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-mono">userPreferences</td>
                        <td className="py-2">365 días</td>
                        <td className="py-2">Configuración de cuenta y preferencias</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Cookies de Terceros</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Algunos servicios externos pueden establecer sus propias cookies:
            </p>
            <ul className="space-y-3">
              <li className="text-slate-700">
                <strong>Google Analytics:</strong> Para análisis de tráfico web
                <br />
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-900 underline">
                  Política de Google
                </a>
              </li>
              <li className="text-slate-700">
                <strong>Google AdSense:</strong> Para mostrar publicidad
                <br />
                <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-900 underline">
                  Cómo Google usa las cookies en publicidad
                </a>
              </li>
              <li className="text-slate-700">
                <strong>Google OAuth:</strong> Para autenticación con Google
                <br />
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-900 underline">
                  Política de privacidad de Google
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Gestionar sus Preferencias de Cookies</h2>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Banner de Cookies</h3>
                <p className="text-slate-700 leading-relaxed">
                  Cuando visita JobSuiza por primera vez, verá un banner de cookies donde puede aceptar o rechazar 
                  cookies no esenciales. Puede cambiar sus preferencias en cualquier momento haciendo clic en 
                  "Configuración de Cookies" en el footer.
                </p>
              </div>

              <div className="bg-slate-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Configuración del Navegador</h3>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Puede configurar su navegador para rechazar todas las cookies o indicar cuándo se envía una cookie:
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-slate-700">
                  <li><strong>Chrome:</strong> Configuración → Privacidad y seguridad → Cookies</li>
                  <li><strong>Firefox:</strong> Opciones → Privacidad y seguridad → Cookies</li>
                  <li><strong>Safari:</strong> Preferencias → Privacidad → Cookies</li>
                  <li><strong>Edge:</strong> Configuración → Cookies y permisos de sitio</li>
                </ul>
                <p className="text-sm text-slate-600 mt-3">
                  <strong>Nota:</strong> Bloquear cookies esenciales impedirá que algunas funciones del sitio funcionen correctamente.
                </p>
              </div>

              <div className="bg-slate-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Desactivar Google Analytics</h3>
                <p className="text-slate-700 leading-relaxed">
                  Puede desactivar Google Analytics instalando el complemento del navegador:{' '}
                  <a 
                    href="https://tools.google.com/dlpage/gaoptout" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-900 underline"
                  >
                    Google Analytics Opt-out
                  </a>
                </p>
              </div>

              <div className="bg-slate-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Desactivar Publicidad Personalizada</h3>
                <p className="text-slate-700 leading-relaxed mb-2">
                  Para desactivar anuncios personalizados de Google:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                  <li>
                    <a href="https://adssettings.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-900 underline">
                      Configuración de anuncios de Google
                    </a>
                  </li>
                  <li>
                    <a href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer" className="text-blue-900 underline">
                      Your Online Choices (Europa)
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Tecnologías Similares</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Además de cookies, utilizamos tecnologías similares como:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>Local Storage:</strong> Para almacenar tokens de autenticación y preferencias de usuario</li>
              <li><strong>Session Storage:</strong> Para datos temporales durante la sesión de navegación</li>
              <li><strong>Web Beacons:</strong> En emails (si en el futuro enviamos newsletters)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Actualizaciones de esta Política</h2>
            <p className="text-slate-700 leading-relaxed">
              Podemos actualizar esta Política de Cookies ocasionalmente para reflejar cambios en las cookies que utilizamos 
              o por razones operativas, legales o regulatorias. Revise esta página periódicamente para estar informado.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Más Información</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Para más información sobre cómo procesamos sus datos personales, consulte nuestra{' '}
              <Link to="/privacy-policy" className="text-blue-900 underline hover:text-blue-700">
                Política de Privacidad
              </Link>.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Si tiene preguntas sobre nuestro uso de cookies, contáctenos en: <strong>info@jobsuiza.com</strong>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200">
          <Link to="/" className="text-blue-900 hover:text-blue-700 underline">
            ← Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
